import { ToastType } from '@components/Toast';
import {
  prepareCustomerDetailsChangeSummary,
  setCustomerDetailsChangeBaseline,
  synchronizeCustomerDetailsChangeHydration,
} from '@changeTracking/pages/customerDetails/formChangeTracking';
import { handleToast, isAdmin, isSuperAdmin } from '@utils/utils';
import { produce } from 'solid-js/store';
import { cloneDeep } from 'lodash';

import {
  addCollectionCommentCall,
  createEdiRequestCall,
  creditIncreaseCall,
  deleteCollectionCommentsCall,
  fetchCustomerData,
  fetchCustomerOfficeGroupsCall,
  fetchEDIConfigIsTenderAutoResponseAuto990Decline,
  fetchEdiConfiguration,
  fetchUploadedAutoAcceptDeclineSheet,
  removeFromWalletApi,
  sendEdiConfigurationData,
  updateCustomerDetailsCall,
} from './services';
import {
  customerDetailsStore,
  setCreditRequestStore,
  setCustomerDetailsStore,
  setEdiConfigurationStore,
} from './store';
import {
  Comment,
  CreateEditRequestPayload,
  CreditRequestState,
  ICreditRequest,
  ICustomerDetails,
  RemoveFromWalletPayloadT,
  ediConfigurationType,
} from './types';

const withTransportationFields = (
  customer: ICustomerDetails,
): ICustomerDetails => ({
  ...customer,
  mcNumber: customer.mcNumber ?? null,
  isAuthorityFreightBroker: customer.isAuthorityFreightBroker ?? null,
  isAuthorityCarrier: customer.isAuthorityCarrier ?? null,
  isAuthorityFreightForwarder: customer.isAuthorityFreightForwarder ?? null,
  isAuthorityDirectShipper: customer.isAuthorityDirectShipper ?? null,
  assetBased: customer.assetBased ?? null,
  isCrossBorder: customer.isCrossBorder ?? null,
});

const normalizeCustomerDetailsForStore = (
  customer: ICustomerDetails,
): ICustomerDetails =>
  withTransportationFields({
    ...customer,
    creditApplications: customer.creditApplications,
    negativeCreditAllowance:
      customer.negativeCreditAllowance && customer.negativeCreditAllowance > 0
        ? -customer.negativeCreditAllowance
        : customer.negativeCreditAllowance,
  });

export const getCustomerData = async (customerId: string) => {
  setCustomerDetailsStore('isLoading', true);
  const resp = await fetchCustomerData(customerId);
  if (resp) {
    const normalizedCustomer = normalizeCustomerDetailsForStore(resp);
    setCustomerDetailsStore(
      produce((draft) => {
        draft.customer = normalizedCustomer;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
    setCustomerDetailsChangeBaseline(normalizedCustomer);
    await fetchCustomerOfficeGroups(
      customerDetailsStore.customer.accountManagerOfficeId!,
    );
  } else {
    setCustomerDetailsStore(
      produce((draft) => {
        draft.isError = true;
        draft.errorMessage = 'Failed to fetch customer details';
      }),
    );
  }
  setCustomerDetailsStore('isLoading', false);
};

export const updateCustomerDetailsState = (
  updates: Partial<ICustomerDetails>,
) => {
  Object.entries(updates).forEach(([key, value]) => {
    setCustomerDetailsStore('customer', key as keyof ICustomerDetails, value);
  });
};

export const applyCustomerDetailsHydration = (apply: () => void) => {
  const before = cloneDeep(customerDetailsStore.customer);
  apply();
  synchronizeCustomerDetailsChangeHydration(
    before,
    customerDetailsStore.customer,
  );
};

export const getEDIConfiguerationsForCustomer = async (customerId: string) => {
  setEdiConfigurationStore(
    produce((draft) => {
      draft.isLoading = true;
    }),
  );
  const response = await fetchEdiConfiguration(customerId);
  if (response && response.isSuccess)
    setEdiConfigurationStore(
      produce((draft) => {
        draft.ediData = response.value;
        draft.isError = false;
        draft.errorMessage = '';
        draft.isLoading = false;
      }),
    );
  else {
    setEdiConfigurationStore(
      produce((draft) => {
        draft.isLoading = false;
        draft.isError = true;
        draft.ediData = {} as ediConfigurationType;
      }),
    );
  }
};

export const updateEdiState = (updates: Partial<ediConfigurationType>) => {
  Object.entries(updates).forEach(([key, value]) => {
    setEdiConfigurationStore(
      'ediData',
      key as keyof ediConfigurationType,
      value,
    );
  });
};

export const saveEdiConfig = async (data: unknown) => {
  setEdiConfigurationStore('isSaveLoading', true);
  const response = await sendEdiConfigurationData(data, () => {
    setEdiConfigurationStore('isSaveLoading', false);
  });
  if (response && response.isSuccess) {
    setEdiConfigurationStore('ediData', response.value);
    handleToast(ToastType.Success, 'Edi Configuration Saved Successfully');
  }
};

export const deleteCollectionComments = async (
  commentId: number,
  cb?: () => void,
) => {
  const resp = await deleteCollectionCommentsCall(commentId);
  const collectionComments = [
    ...(customerDetailsStore.customer.collectionComments || []),
  ].filter((c) => c.id !== commentId);
  if (Boolean(resp))
    applyCustomerDetailsHydration(() => {
      setCustomerDetailsStore(
        'customer',
        'collectionComments',
        collectionComments,
      );
    });
  cb && cb();
};

export const addCollectionComment = async (
  commentId: number,
  comment: string,
  cb?: () => void,
) => {
  const resp = await addCollectionCommentCall(commentId, comment);
  if (Boolean(resp)) {
    const collectionComments = [
      ...(customerDetailsStore.customer.collectionComments || []),
    ];
    collectionComments.unshift(resp as Comment);
    applyCustomerDetailsHydration(() => {
      setCustomerDetailsStore(
        'customer',
        'collectionComments',
        collectionComments,
      );
    });
  }
  cb && cb();
};

export const creditIncrease = async (
  payload: ICreditRequest,
  cb?: () => void,
) => {
  setCreditRequestStore('isLoading', true);
  const res = await creditIncreaseCall(payload);
  if (res && res.isSuccess) {
    cb && cb();
    setCreditRequestStore(
      produce((draft: CreditRequestState) => {
        draft.isError = false;
        draft.errorMessage = '';
        draft.isLoading = false;
      }),
    );
  } else {
    setCreditRequestStore(
      produce((draft: CreditRequestState) => {
        draft.isError = true;
        draft.errorMessage = 'Failed to request credit';
        draft.isLoading = false;
        draft.creditRequest = {} as ICreditRequest;
      }),
    );
  }
};

export const updateCustomerDetails = async (payload: ICustomerDetails) => {
  setCustomerDetailsStore('isLoading', true);
  const { changedForms } = prepareCustomerDetailsChangeSummary(payload);
  const customerResp = await updateCustomerDetailsCall(
    withTransportationFields(payload),
    changedForms,
  );
  if (Boolean(customerResp)) {
    const normalizedCustomer = normalizeCustomerDetailsForStore(
      customerResp as ICustomerDetails,
    );
    setCustomerDetailsStore(
      produce((draft) => {
        draft.customer = normalizedCustomer;
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
    setCustomerDetailsChangeBaseline(normalizedCustomer);
    setCustomerDetailsStore('isLoading', false);
    return true;
  }
  setCustomerDetailsStore(
    produce((draft) => {
      draft.isError = true;
      draft.isLoading = false;
    }),
  );
  return false;
};

export const createEdiRequest = async (payload: CreateEditRequestPayload) => {
  try {
    return await createEdiRequestCall(payload);
  } catch (error) {
    handleToast(ToastType.Error, 'Failed to create edi request');
    throw new Error('Failed to create edi request');
  }
};

export const fetchCustomerOfficeGroups = async (
  officeId: number,
  accountId?: number,
) => {
  const res = await fetchCustomerOfficeGroupsCall(officeId);

  if (res && res.isSuccess) {
    if (
      officeId ==
        (accountId ?? customerDetailsStore.customer.accountManagerOfficeId) ||
      officeId == customerDetailsStore.customer.salesManagerOfficeId
    ) {
      res.value = res.value.filter(
        (x) => x.name != 'Managers' && x.name != 'Admin',
      );
    }
    if (!isAdmin() && !isSuperAdmin()) {
      res.value = res.value.filter((x) => x.name !== 'The Office');
    }
    const assignOfficeGroupRes = res.value.map((item) => {
      return {
        ...item,
        label: `${item.name as string} (${item.office})`,
        value: item.id,
      };
    });
    setCustomerDetailsStore('assignOfficeGroup', assignOfficeGroupRes);
  }
};

export const getEDIConfigIsTenderAutoResponseAuto990Decline = async (
  customerId: string,
) => {
  try {
    const res =
      await fetchEDIConfigIsTenderAutoResponseAuto990Decline(customerId);
    if (res && res.isSuccess) {
      return res;
    }
  } catch (error) {
    return undefined;
  }
};

export const getUploadedAutoAcceptDeclineSheet = async (customerId: string) => {
  try {
    const res = await fetchUploadedAutoAcceptDeclineSheet(customerId);
    if (res) {
      return res.value;
    }
  } catch (error) {
    return undefined;
  }
};

export const removeFromWalletAction = async (
  payload: RemoveFromWalletPayloadT,
) => {
  const success = await removeFromWalletApi(payload);
  if (Boolean(success)) {
    setCustomerDetailsStore(
      produce((draft) => {
        draft.customer.claimedBy = null;
        draft.customer.claimedById = null;
      }),
    );
    handleToast(ToastType.Success, 'Removed from Agents Wallet');
  }
};
